import React from 'react';
import { FaChartBar, FaExclamationTriangle } from 'react-icons/fa';

/**
 * Usage Indicator Component
 * Displays usage statistics with visual feedback
 * Follows Single Responsibility Principle - only handles usage display
 * 
 * @param {Object} usage - Usage data {feature, used, limit}
 * @param {boolean} showWarning - Show warning when near limit
 * @param {Function} onUpgrade - Callback for upgrade action
 */
const UsageIndicator = ({ 
  usage, 
  showWarning = true, 
  onUpgrade 
}) => {
  const { feature, used = 0, limit = 0 } = usage || {};
  
  // Calculate percentage
  const percentage = limit > 0 ? Math.min((used / limit) * 100, 100) : 0;
  
  // Determine status and color
  const getStatus = () => {
    if (percentage >= 100) return { color: 'red', text: 'Limit Reached', icon: FaExclamationTriangle };
    if (percentage >= 80) return { color: 'yellow', text: 'Approaching Limit', icon: FaExclamationTriangle };
    if (percentage >= 50) return { color: 'blue', text: 'Healthy Usage', icon: FaChartBar };
    return { color: 'green', text: 'Plenty Available', icon: FaChartBar };
  };

  const status = getStatus();

  // Color classes
  const colorClasses = {
    red: {
      bg: 'bg-red-50',
      border: 'border-red-200',
      text: 'text-red-700',
      bar: 'bg-red-500',
      icon: 'text-red-500',
    },
    yellow: {
      bg: 'bg-yellow-50',
      border: 'border-yellow-200',
      text: 'text-yellow-700',
      bar: 'bg-yellow-500',
      icon: 'text-yellow-500',
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-700',
      bar: 'bg-blue-500',
      icon: 'text-blue-500',
    },
    green: {
      bg: 'bg-green-50',
      border: 'border-green-200',
      text: 'text-green-700',
      bar: 'bg-green-500',
      icon: 'text-green-500',
    },
  };

  const colors = colorClasses[status.color];
  const StatusIcon = status.icon;

  return (
    <div className={`${colors.bg} border ${colors.border} rounded-lg p-4`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <StatusIcon className={`${colors.icon}`} />
          <span className={`font-semibold ${colors.text}`}>
            {feature || 'Feature Usage'}
          </span>
        </div>
        <span className="text-sm text-gray-600">
          {used} / {limit === -1 ? '∞' : limit}
        </span>
      </div>

      {/* Progress Bar */}
      <div className="mb-3">
        <div className="w-full bg-gray-200 rounded-full h-3 overflow-hidden">
          <div
            className={`h-full transition-all duration-500 ${colors.bar}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>

      {/* Status Message */}
      <div className="flex items-center justify-between">
        <span className={`text-xs ${colors.text} font-medium`}>
          {status.text}
        </span>
        <span className={`text-xs ${colors.text}`}>
          {percentage.toFixed(0)}% used
        </span>
      </div>

      {/* Warning / Upgrade Prompt */}
      {showWarning && percentage >= 80 && onUpgrade && (
        <div className="mt-3 pt-3 border-t border-gray-200">
          <button
            onClick={onUpgrade}
            className="w-full bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white text-sm font-semibold py-2 px-4 rounded-lg transition-all duration-200"
          >
            {percentage >= 100 ? 'Upgrade for More' : 'Upgrade Now'}
          </button>
        </div>
      )}
    </div>
  );
};

export default UsageIndicator;
